import {
  notUndefined,
  useVirtualizer,
  VirtualItem,
  Virtualizer,
} from '@tanstack/react-virtual';
import { RefObject, useCallback } from 'react';

import { SCROLL_BUFFER_ROW_COUNT } from '@/components/table/constants';

/**
 * ZOOM FIX (CHARIOT-1377 Bug 3): snap a browser-measured row height to the
 * intended `rowHeight` when it is within ±2px. At extreme zoom levels browsers
 * report subpixel-different heights that, accumulated across hundreds of rows,
 * drift the virtualizer's total size. Snapping prevents that drift while still
 * allowing legitimate height variations (expanded/error rows).
 *
 * Extracted verbatim from the inline `measureElement` arithmetic so it can be
 * unit-tested directly.
 */
export function snapMeasuredHeight(
  measured: number,
  rowHeight: number
): number {
  return Math.abs(measured - rowHeight) <= 2 ? rowHeight : measured;
}

/**
 * Buffer-row count rule (CHARIOT-1377 Bug 4). Buffer rows are invisible spacers
 * appended to the virtual scroll area so the last data row isn't obscured by
 * bottom padding. They are only added when there is no further page to load,
 * nothing is currently loading, and there is at least one row of data.
 *
 * Extracted verbatim from the inline rule so it can be unit-tested directly.
 */
export function computeBufferRowCount(args: {
  hasNextPage: boolean | undefined;
  isLoading: boolean;
  dataLength: number;
}): number {
  const { hasNextPage, isLoading, dataLength } = args;
  return !hasNextPage && !isLoading && dataLength > 0
    ? SCROLL_BUFFER_ROW_COUNT
    : 0;
}

interface UseTableVirtualizerOptions {
  parentRef: RefObject<HTMLElement | null>;
  rowHeight: number;
  loadingRowCount: number;
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  /** Number of rows to virtualize (i.e. groupedData.length). */
  dataLength: number;
}

interface UseTableVirtualizerResult {
  virtualizer: Virtualizer<HTMLElement, Element>;
  items: VirtualItem[];
  before: number;
  after: number;
  bufferRowCount: number;
}

/**
 * Row virtualizer + spacer geometry for the data table (CHARIOT-1377 Bug 3 + 4).
 *
 * Extracted verbatim from Table.tsx so both Table and the canonical TanStack
 * table can share one implementation. Behavior is byte-identical to the inline
 * blocks it replaces: the ±2px `measureElement` zoom snap, `estimateSize`,
 * `overscan: 5`, `scrollMargin: 100`, the `bufferRowCount` rule, and the
 * before/after spacer computation all move here unchanged.
 *
 * IMPORTANT — the caller MUST wire the per-row ref callback:
 *
 *     ref={el => virtualizer.measureElement(el)}
 *
 * on each rendered `<TableBody>` row (see Table.tsx's tbody map). The ±2px zoom
 * snap lives INSIDE `measureElement`, but `measureElement` only runs when the
 * row ref invokes it. This hook returns `virtualizer` but cannot attach that
 * ref for the caller. If the caller omits the ref wiring, the virtualizer falls
 * back to `estimateSize` only and Bug 3 (zoom row-height drift, 25%–200% zoom)
 * silently regresses.
 *
 * @see Table.zoom.test.tsx - zoom regression suite (Bug 3)
 * @see Table.pagination.test.tsx - bottom-padding regression suite (Bug 4)
 */
export function useTableVirtualizer({
  parentRef,
  rowHeight,
  loadingRowCount,
  isLoading,
  hasNextPage,
  dataLength,
}: UseTableVirtualizerOptions): UseTableVirtualizerResult {
  /**
   * Calculate buffer row count based on pagination state.
   *
   * Buffer rows extend the virtual scroll area to prevent the last data row
   * from being cut off by bottom padding. They are only rendered when:
   * - hasNextPage is false (no more data to load)
   * - isLoading is false (not currently fetching)
   * - groupedData has items (not empty state)
   *
   * Buffer rows are invisible spacers that increase getTotalSize() by
   * SCROLL_BUFFER_ROW_COUNT * rowHeight, allowing users to scroll past
   * the last data row without it being obscured by padding.
   */
  const bufferRowCount = computeBufferRowCount({
    hasNextPage,
    isLoading,
    dataLength,
  });

  /**
   * Virtualizer for efficient rendering of large datasets.
   *
   * Uses measureElement in addition to estimateSize to fix zoom-related bugs.
   * At extreme zoom levels (60% zoom OUT), estimateSize alone caused row heights
   * to be calculated as 8996px instead of 52px due to CSS pixel multiplication.
   *
   * measureElement uses offsetHeight which returns zoom-independent measurements
   * from actual DOM elements, overriding the estimateSize calculation.
   *
   * Performance: ~1-2ms overhead compared to estimateSize only (negligible)
   * Zoom Range: Tested 50%-200% zoom levels
   *
   * @see https://tanstack.com/virtual/v3/docs/api/virtualizer#measureelement
   */
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual returns non-memoizable functions (expected behavior)
  const virtualizer = useVirtualizer({
    // Add buffer rows to count so getTotalSize() includes buffer space
    count: isLoading ? loadingRowCount : dataLength + bufferRowCount,
    getScrollElement: useCallback(() => {
      return parentRef.current;
    }, []),
    estimateSize: useCallback(() => rowHeight, [rowHeight]),
    measureElement: useCallback(
      (el: Element) => {
        // Use offsetHeight for accurate measurement at all zoom levels
        // This is more reliable than getBoundingClientRect().height
        const measured =
          el instanceof HTMLElement ? el.offsetHeight : rowHeight;

        // ZOOM FIX: At extreme zoom levels (67%, 88%, 94%), browsers may report
        // heights slightly different from the intended rowHeight due to subpixel rendering.
        // Example: At 67% zoom with rowHeight=64px:
        //   - Intended: 64px
        //   - Browser reports: 63.89px or 64.11px
        //
        // If we use the browser's measurement, TanStack Virtual will accumulate these
        // tiny differences across hundreds of rows, causing the virtualizer's total size
        // to drift from reality. This manifests as:
        //   - Scroll position jumping
        //   - Incorrect "after" padding calculations
        //   - Last rows appearing cut off
        //
        // Solution: If the measured height is within 2px of rowHeight, snap to rowHeight.
        // This prevents accumulation of subpixel errors while still allowing legitimate
        // height variations (e.g., expanded rows, error states).
        return snapMeasuredHeight(measured, rowHeight);
      },
      [rowHeight]
    ),
    overscan: 5,
    scrollMargin: 100,
  });

  const items = virtualizer.getVirtualItems();
  const [before, after] =
    items.length > 0
      ? [
          notUndefined(items[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(items[items.length - 1]).end,
        ]
      : [0, 0];

  return { virtualizer, items, before, after, bufferRowCount };
}
